import { ColumnTypeOverride } from '@helios/filter-modal'
import { ColumnType } from '../../models/field-catalog.model'
import {
  QueryFilterType,
  QuerySortType,
} from '../../models/transactions-query.model'
import {
  AGGREGATE_INFO_GROUP,
  CatalogCellType,
  ColumnCatalogEntry,
} from '../../models/source-definition.model'

/**
 * The one place the /query contract's TWO type vocabularies are derived.
 *
 * Ported from `libs/panels/positions-2/src/lib/grid/query/field-types.ts` (PVAL-26129 §4). The only
 * substantive difference is how "the user owns this column's type" is detected: positions-2 keys on
 * `storage === 'AGG_INFO'`, and the transactions catalogs publish no `storage` at all, so this keys
 * on the AGGREGATE_INFO group instead (§4: "Transactions has no storage, so use the group key").
 *
 * The contract spells the same concept differently on a filter (`filterType`: lowercase, and with a
 * `set` member the sort side has no equivalent for) and on a sort (`type`: uppercase, with a `BOOL`
 * member the filter side has no equivalent for). They are therefore mapped through explicit tables
 * rather than by upper-casing one into the other — the two disagree on exactly the members that
 * matter.
 *
 * Sending NEITHER is the failure this file exists to prevent: an omitted sort type makes the backend
 * fall back to a text comparison, so a numeric column silently sorts 1000 before 9.
 */

/** Catalog {@link ColumnType} -> the filter vocabulary. */
const FILTER_TYPE_BY_COLUMN_TYPE: Record<ColumnType, QueryFilterType> = {
  TEXT: 'text',
  NUM: 'number',
  DATE: 'date',
  DATETIME: 'datetime',
  // The filter vocabulary has no `bool` member: a boolean column is filtered as a closed set of two
  // values, which is also how the grid presents it (see the col-def factory's `boolean` branch).
  BOOL: 'set',
  // An id list filters by array overlap through a `SetFilter<String>` slot.
  TEXT_LIST: 'set',
}

/** Catalog {@link ColumnType} -> the sort vocabulary. */
const SORT_TYPE_BY_COLUMN_TYPE: Record<ColumnType, QuerySortType> = {
  TEXT: 'TEXT',
  NUM: 'NUM',
  DATE: 'DATE',
  DATETIME: 'DATETIME',
  BOOL: 'BOOL',
  // Never actually sent: a TEXT_LIST column is unsortable (see `sortable` in buildFieldMeta). The
  // entry exists only so the table stays total over ColumnType.
  TEXT_LIST: 'TEXT',
}

/** A user-chosen override -> the filter vocabulary. */
const FILTER_TYPE_BY_OVERRIDE: Record<ColumnTypeOverride, QueryFilterType> = {
  text: 'text',
  number: 'number',
  date: 'date',
}

/** A user-chosen override -> the sort vocabulary. */
const SORT_TYPE_BY_OVERRIDE: Record<ColumnTypeOverride, QuerySortType> = {
  text: 'TEXT',
  number: 'NUM',
  date: 'DATE',
}

/**
 * Recover a catalog {@link ColumnType} from the lossy rendering hint, for the in-repo fixture
 * catalogs that describe columns by `cellType` only.
 *
 * `money` and `set` are the lossy pair: both are UI concepts with no API type behind them, so they
 * map back to the type the API would actually have sent (NUM and TEXT respectively). A real catalog
 * column never needs this — the adapter carries `valueType` straight off the descriptor.
 */
const COLUMN_TYPE_BY_CELL_TYPE: Record<CatalogCellType, ColumnType> = {
  text: 'TEXT',
  number: 'NUM',
  money: 'NUM',
  set: 'TEXT',
  date: 'DATE',
  datetime: 'DATETIME',
  boolean: 'BOOL',
  list: 'TEXT_LIST',
}

/**
 * An unknown catalog type falls back to TEXT, which is the same thing the backend does when the type
 * is omitted — so an unrecognised value degrades to the documented default rather than to a 400.
 */
const UNKNOWN_TYPE: ColumnType = 'TEXT'

export const filterTypeOfColumnType = (
  type: ColumnType | undefined,
): QueryFilterType =>
  FILTER_TYPE_BY_COLUMN_TYPE[type ?? UNKNOWN_TYPE] ??
  FILTER_TYPE_BY_COLUMN_TYPE[UNKNOWN_TYPE]

export const sortTypeOfColumnType = (
  type: ColumnType | undefined,
): QuerySortType =>
  SORT_TYPE_BY_COLUMN_TYPE[type ?? UNKNOWN_TYPE] ??
  SORT_TYPE_BY_COLUMN_TYPE[UNKNOWN_TYPE]

/**
 * The catalog type for an entry: the descriptor's own `valueType` when the adapter supplied it, and
 * otherwise recovered from the rendering hint.
 */
export const columnTypeOf = (entry: ColumnCatalogEntry): ColumnType =>
  entry.valueType ?? COLUMN_TYPE_BY_CELL_TYPE[entry.cellType] ?? UNKNOWN_TYPE

/**
 * Whether a column's comparison type is user-owned rather than catalog-owned.
 *
 * Prometheus publishes every AGGREGATE_INFO key as TEXT because the blob is untyped, so the user
 * picks the type (§4). Enfusion `tran_data` columns are ALSO dynamic but are accurately typed from
 * RPS metadata, so they get no picker and keep their catalog type — which is why this keys on the
 * group and not on `isDynamic`.
 */
export const isTypeOverridable = (entry: ColumnCatalogEntry): boolean =>
  entry.colSource === AGGREGATE_INFO_GROUP

/**
 * Everything the request builder needs to know about one field. Deliberately a flat record rather
 * than the catalog entry itself: the builder must not be able to reach `cellType`, which collapses
 * NUM and MONEY and would silently produce the wrong sort `type`.
 */
export interface QueryFieldMeta {
  field: string
  filterType: QueryFilterType
  sortType: QuerySortType
  /** The routing bucket — see `request-filter.builder.ts`. */
  isDynamic: boolean
  /** Whether this field's type came from the user rather than the catalog. */
  overridable: boolean
  /**
   * Whether the backend will accept a sort on this field. False only for a STATIC field the read
   * model declares unsortable, which 400s the whole request (see `STATIC_UNSORTABLE_FIELDS`).
   */
  sortable: boolean
}

/**
 * Resolve per-field query metadata for a source's catalog.
 *
 * `overrideFor` is consulted ONLY for Aggregate Info columns. Every other column's catalog type is
 * trustworthy per the contract, so an override on one of those is ignored rather than honoured —
 * that guards against a stale choice (written when a column WAS agg-stored, or made against a
 * same-named column on the other source) retyping a column that works today.
 *
 * UI-owned columns — the computed Helios columns, the match-count columns the UI keys by target —
 * are absent from the catalog and therefore absent from this map, which is what keeps them out of
 * the request entirely (§5). The backend does not know those names; a filter on one is silently
 * ignored and a sort on one silently sorts on nulls, so neither failure would be visible.
 *
 * Resolve this per request and never cache it: a type can change between two fetches.
 *
 * `unsortable` names the source's STATIC fields the backend refuses to sort. It is consulted only
 * for static columns: a dynamic key that shares one of those names is a blob key and sorts fine.
 */
export function buildFieldMeta(
  columns: readonly ColumnCatalogEntry[],
  overrideFor: (field: string) => ColumnTypeOverride | undefined = () =>
    undefined,
  unsortable: ReadonlySet<string> = new Set<string>(),
): Map<string, QueryFieldMeta> {
  const meta = new Map<string, QueryFieldMeta>()
  columns.forEach(column => {
    const overridable = isTypeOverridable(column)
    const override = overridable ? overrideFor(column.field) : undefined
    const valueType = columnTypeOf(column)

    meta.set(column.field, {
      field: column.field,
      filterType: override
        ? FILTER_TYPE_BY_OVERRIDE[override]
        : filterTypeOfColumnType(valueType),
      sortType: override
        ? SORT_TYPE_BY_OVERRIDE[override]
        : sortTypeOfColumnType(valueType),
      isDynamic: column.isDynamic,
      overridable,
      // A TEXT_LIST column is never sortable — the catalog has no `sortable` flag, but the backend
      // rejects a sort on an id array with a 400 for the whole request. Otherwise only a STATIC field
      // on the source's unsortable list is.
      sortable:
        valueType !== 'TEXT_LIST' &&
        (column.isDynamic || !unsortable.has(column.field)),
    })
  })
  return meta
}
